import { InAppState } from "../base/InAppState";
import { DragSort } from "../../core/DragSort";
import { Planner } from "../../core/Planner";
import { _lcs } from "../../i18n";

import boardTemplate from "./PlanTemplate.html";
import emptyTemplate from "./PlanEmptyTemplate.html";
import formTemplate from "./PlanFormTemplate.html";

/**
 * PlanState — the "Planer" tab.
 *
 * Three views behind one route, switched on the instance rather than by
 * navigating: the empty introduction, the create/edit form, and the board
 * itself. They are one screen because that is what they are to a guest — the
 * form is a step in making a plan, not a place — and because the board is the
 * only thing anyone comes here for. Sub-routes would also mean `/plan/new`
 * competing with `/plan/:id` for the same path shape, which the router settles
 * by declaration order rather than by intent.
 *
 * The board is: an unsorted list of everything added with "Zum Planer
 * hinzufügen", one section per day of the stay, and the suggestions for that
 * span. Entries move between days by dragging the grip (core/DragSort) or with
 * the day picker on each row; the chevrons reorder within a day. Picker and
 * chevrons stay because a drag has no keyboard equivalent.
 *
 * An event whose date falls in the stay arrives on its day already (the API
 * places it). Moving it to another day is allowed — the guest may know
 * better — but only after a question (_confirmOffDate, Funktionstest F12).
 *
 * Re-rendering the whole screen after each change is deliberate: a plan is a
 * few dozen rows, the server's answer is the truth, and painting from it is how
 * the screen and the plan cannot disagree. The one exception is a drag, where
 * the rows have already moved on screen — see _onDragCommit.
 */
export class PlanState extends InAppState
{
    static ROUTE = "/plan";
    static ID = "plan";

    /** A new plan is offered as a long weekend — the common case, and editable. */
    static DEFAULT_NIGHTS = 2;

    getActiveTab() { return "plan"; }

    /**
     * The Planer belongs to a person, so it needs a confirmed account — see
     * App.hasVerifiedAccount. Without one the guest is sent to
     * VerifyRequiredState, which explains what is missing.
     */
    canEnter()
    {
        return super.canEnter() && this.app.hasVerifiedAccount();
    }

    getRedirectUrl()
    {
        // Not signed in at all is still the login's job; the gate is for a
        // guest who IS in the app but has no confirmed account.
        return this.app.user === null ? super.getRedirectUrl() : "/verify-account?from=plan";
    }

    async enter()
    {
        this.planner = this.app.planner;
        this.busy = false;

        // Arriving from a detail page that had nothing to add to yet ("Zum
        // Planer hinzufügen" with no plan) — open on the form, and add the
        // record the guest was looking at once the plan exists.
        this.pendingAdd = PlanState.pendingAdd || null;
        PlanState.pendingAdd = null;

        // Once, before anything renders — see _bind.
        this._bind();

        await this.planner.loadPlans();
        if ( !this.isActive() )
        {
            return;
        }

        if ( this.planner.plans.length > 0 )
        {
            await this.planner.open();
            // A plan deleted on another device (or swept by the retention job)
            // is dropped from the list by open() — fall through to the next one
            // rather than showing "no plans" to a guest who has several.
            if ( !this.planner.plan && this.planner.plans.length > 0 )
            {
                await this.planner.open();
            }
        }
        if ( !this.isActive() )
        {
            return;
        }

        if ( this.pendingAdd && !this.planner.plan )
        {
            this._renderForm();
            return;
        }

        this._render();

        if ( this.pendingAdd )
        {
            await this._addSuggested( this.pendingAdd.kind, this.pendingAdd.recordId );
            this.pendingAdd = null;
        }
    }

    async exit()
    {
        // A drag in flight holds listeners on `window` that the State's tracked
        // cleanup does not reach — see DragSort.destroy.
        if ( this.sorter )
        {
            this.sorter.destroy();
            this.sorter = null;
        }
        await super.exit();
    }

    /**
     * Ask the planner to open on the create form with a record waiting to be
     * added — how a detail screen hands a guest with no plan over.
     */
    static requestAdd( kind, recordId )
    {
        PlanState.pendingAdd = { "kind" : kind, "recordId" : recordId };
    }

    /* -------------------------------- Rendering ------------------------------ */

    _render()
    {
        if ( !this.planner.plan )
        {
            this.renderScreen( emptyTemplate, { "failed" : this.planner.plansFailed } );
            return;
        }

        this.renderScreen( boardTemplate, {
            "board" : this.planner.getBoard(),
            "plans" : this.planner.plans,
            "dayOptions" : this.planner.getDayOptions()
        } );
    }

    /**
     * @param {boolean} isEdit  Editing the open plan, rather than creating one.
     */
    _renderForm( isEdit = false )
    {
        const plan = isEdit ? this.planner.plan : this._blankPlan();

        this.renderScreen( formTemplate, {
            "isEdit" : isEdit,
            "plan" : {
                "name" : plan.name,
                "startDate" : plan.startDate,
                "endDate" : plan.endDate
            },
            // With no plan at all there is nothing to go back to.
            "canCancel" : !!this.planner.plan
        } );
    }

    _blankPlan()
    {
        const today = Planner.today();
        const end = new Date( Planner.toDate( today ) );
        end.setDate( end.getDate() + PlanState.DEFAULT_NIGHTS );

        return {
            "name" : this.planner.defaultPlanName(),
            "startDate" : today,
            "endDate" : [
                end.getFullYear(),
                String( end.getMonth() + 1 ).padStart( 2, "0" ),
                String( end.getDate() ).padStart( 2, "0" )
            ].join( "-" )
        };
    }

    /* -------------------------------- Binding -------------------------------- */

    /**
     * Wire the screen ONCE, on enter.
     *
     * Everything is delegated from the shell's page-content slot, which is the
     * one element here that survives a render: the screen replaces its own
     * innerHTML after every change, but the slot itself is not touched. Binding
     * to elements inside it — or attaching DragSort to it — from _render would
     * therefore add a fresh set of listeners on every change while the previous
     * ones stayed live (the State only disposes tracked listeners on exit, not
     * on re-render), so by the third edit each click would fire three times and
     * three DragSort instances would fight over one drag.
     */
    _bind()
    {
        const root = this.app.view.pageContent;

        this.addEventListener( root, "click", ( event ) => this._onClick( event ) );

        this.addEventListener( root, "change", ( event ) =>
        {
            const picker = event.target.closest( '[data-id="plan-switch"]' );
            if ( picker )
            {
                this._switchPlan( picker.value );
                return;
            }

            const dayPicker = event.target.closest( '[data-id="item-day"]' );
            const dayRow = dayPicker && dayPicker.closest( "[data-item]" );
            if ( dayRow )
            {
                this._moveToDay( dayRow.getAttribute( "data-item" ), dayPicker.value || null );
                return;
            }

            // A guest who moves the arrival past the departure means to move
            // both; dragging the second field after it is busywork.
            if ( event.target.id === "plan-start" )
            {
                const end = root.querySelector( "#plan-end" );
                if ( end && event.target.value && end.value && end.value < event.target.value )
                {
                    end.value = event.target.value;
                }
            }
        } );

        // `submit` bubbles, so the form can be replaced freely underneath.
        this.addEventListener( root, "submit", ( event ) =>
        {
            const form = event.target.closest( ".planform" );
            if ( !form ) return;
            event.preventDefault();
            this._submitForm( form.getAttribute( "data-mode" ) === "edit", form );
        } );

        // A row's text behaves as a link; Enter and Space have to work on it.
        this.addEventListener( root, "keydown", ( event ) =>
        {
            if ( event.key !== "Enter" && event.key !== " " ) return;
            const link = event.target.closest( "[data-route]" );
            if ( !link ) return;
            event.preventDefault();
            this.app.router.redirect( link.getAttribute( "data-route" ) );
        } );

        this.sorter = DragSort.attach( this, root, {
            "scroller" : root,
            "onCommit" : ( placements ) => this._onDragCommit( placements )
        } );
    }

    _onClick( event )
    {
        const control = event.target.closest( "[data-act]" );
        if ( control )
        {
            const action = control.getAttribute( "data-act" );

            if ( action === "new" )    { this._renderForm( false ); return; }
            if ( action === "edit" )   { this._renderForm( true );  return; }
            if ( action === "cancel" ) { this._render();            return; }
            if ( action === "delete" ) { this._deletePlan();        return; }

            const suggestion = control.closest( "[data-suggest]" );
            if ( action === "add" && suggestion )
            {
                const [ kind, recordId ] = suggestion.getAttribute( "data-suggest" ).split( ":" );
                this._addSuggested( kind, recordId );
                return;
            }

            const row = control.closest( "[data-item]" );
            if ( !row )
            {
                return;
            }
            const itemId = row.getAttribute( "data-item" );

            if ( action === "up" )     this._nudge( itemId, -1 );
            if ( action === "down" )   this._nudge( itemId, 1 );
            if ( action === "remove" ) this._remove( itemId );
            return;
        }

        // Tapping the row itself opens what it points at.
        const link = event.target.closest( "[data-route]" );
        if ( link )
        {
            this.app.router.redirect( link.getAttribute( "data-route" ) );
        }
    }

    /* --------------------------------- Actions ------------------------------- */

    async _switchPlan( planId )
    {
        if ( !planId || planId === this.planner.plan?.id )
        {
            return;
        }

        await this._guard( async () =>
        {
            await this.planner.open( planId );
            this._render();
        }, _lcs( "Der Reiseplan konnte nicht geöffnet werden." ) );
    }

    async _submitForm( isEdit, form )
    {
        const errorEl = form.querySelector( '[data-id="form-error"]' );
        const submit = form.querySelector( 'button[type="submit"]' );

        const name = ( form.querySelector( "#plan-name" ).value || "" ).trim();
        const startDate = form.querySelector( "#plan-start" ).value;
        const endDate = form.querySelector( "#plan-end" ).value;

        const problem = PlanState._validate( name, startDate, endDate );
        if ( problem )
        {
            errorEl.textContent = problem;
            errorEl.hidden = false;
            return;
        }
        errorEl.hidden = true;

        submit.disabled = true;
        submit.setAttribute( "aria-busy", "true" );

        try
        {
            if ( isEdit )
            {
                const before = this.planner.plan;
                const changes = { "name" : name };
                // The two bounds travel together (the API pairs them) and only
                // when they actually moved — a rename should not reshuffle a
                // board the guest has already arranged.
                if ( startDate !== before.startDate || endDate !== before.endDate )
                {
                    changes.startDate = startDate;
                    changes.endDate = endDate;
                }

                const updated = await this.planner.update( before.id, changes );
                this._render();

                if ( updated.displacedItems > 0 )
                {
                    this.app.view.toast(
                        _lcs( "{count} Einträge liegen außerhalb des neuen Zeitraums und sind jetzt wieder ohne Tag.",
                            { count : updated.displacedItems, n : updated.displacedItems } ),
                        "info", 5200 );
                }
                else
                {
                    this.app.view.toast( _lcs( "Reiseplan gespeichert." ), "success" );
                }
            }
            else
            {
                await this.planner.create( { name, startDate, endDate } );
                this._render();
                this.app.view.toast( _lcs( "Reiseplan angelegt." ), "success" );

                if ( this.pendingAdd )
                {
                    await this._addSuggested( this.pendingAdd.kind, this.pendingAdd.recordId );
                    this.pendingAdd = null;
                }
            }
        }
        catch ( e )
        {
            submit.disabled = false;
            submit.removeAttribute( "aria-busy" );
            errorEl.textContent = e.message;
            errorEl.hidden = false;
        }
    }

    static _validate( name, startDate, endDate )
    {
        if ( !name )
        {
            return _lcs( "Bitte gib deinem Reiseplan einen Namen." );
        }
        if ( !startDate || !endDate )
        {
            return _lcs( "Bitte gib den Zeitraum deiner Reise an." );
        }
        if ( endDate < startDate )
        {
            return _lcs( "Die Abreise kann nicht vor der Anreise liegen." );
        }

        // Mirrors Plan.MAX_DAYS on the API — caught here so the guest gets a
        // sentence they can read instead of a rejected request.
        const days = Math.round(
            ( Planner.toDate( endDate ) - Planner.toDate( startDate ) ) / 86400000 ) + 1;
        if ( days > 60 )
        {
            return _lcs( "Ein Reiseplan kann höchstens 60 Tage umfassen." );
        }
        return null;
    }

    async _deletePlan()
    {
        const plan = this.planner.plan;
        if ( !plan )
        {
            return;
        }

        const confirmed = await this.app.view.confirm( {
            icon : "trash-2",
            title : _lcs( "Reiseplan löschen?" ),
            text : _lcs( "„{name}“ und alle Einträge darin werden entfernt. " +
                         "Das lässt sich nicht rückgängig machen.", { name : plan.name } ),
            confirmLabel : _lcs( "Endgültig löschen" )
        } );
        if ( !confirmed )
        {
            return;
        }

        await this._guard( async () =>
        {
            await this.planner.remove( plan.id );
            if ( this.planner.plans.length > 0 )
            {
                await this.planner.open();
            }
            this._render();
            this.app.view.toast( _lcs( "Reiseplan gelöscht." ), "success" );
        }, _lcs( "Der Reiseplan konnte nicht gelöscht werden." ) );
    }

    /**
     * Move a row one place within its own list.
     *
     * Deliberately does NOT cross into the previous or next day at the ends:
     * "nach oben" on the first entry of Tuesday meaning "onto Monday" is a
     * surprise, and moving between days is what the drag and the day picker
     * are for.
     */
    async _nudge( itemId, direction )
    {
        const board = this.planner.getBoard();
        const item = ( this.planner.plan.items || [] ).find( ( entry ) => entry.id === itemId );
        if ( !item )
        {
            return;
        }

        // The stay markers are excluded up front rather than filtered out
        // afterwards: they always bracket the day, so counting them would make
        // "nach oben" on the first movable row a swap with check-in — a button
        // press that renumbers nothing and appears to do nothing at all.
        const list = ( item.dayDate === null
            ? board.unsorted
            : ( board.days.find( ( day ) => day.date === item.dayDate )?.items || [] )
        ).filter( ( row ) => !row.isFixed );

        const from = list.findIndex( ( row ) => row.id === itemId );
        const to = from + direction;
        if ( from === -1 || to < 0 || to >= list.length )
        {
            return;
        }

        const reordered = list.slice();
        [ reordered[ from ], reordered[ to ] ] = [ reordered[ to ], reordered[ from ] ];

        const placements = reordered.map( ( row, index ) => ( {
            "itemId" : row.id,
            "dayDate" : item.dayDate,
            "sortOrder" : index
        } ) );

        await this._guard( async () =>
        {
            await this.planner.commitLayout( this.planner.plan.id, placements );
            this._render();
        }, _lcs( "Der Eintrag konnte nicht verschoben werden." ) );
    }

    /**
     * The row's day picker: onto the end of the chosen day, or back to the
     * list without a day. An event leaving its date asks first, like a drag.
     */
    async _moveToDay( itemId, dayDate )
    {
        const item = ( this.planner.plan.items || [] ).find( ( entry ) => entry.id === itemId );
        if ( !item || item.dayDate === dayDate )
        {
            return;
        }

        if ( !( await this._confirmOffDate( itemId, dayDate ) ) )
        {
            // The select already shows the new day; put it back.
            this._render();
            return;
        }

        const board = this.planner.getBoard();
        const target = dayDate === null
            ? board.unsorted
            : ( board.days.find( ( day ) => day.date === dayDate )?.items || [] );
        const sortOrder = target.filter( ( row ) => !row.isFixed ).length;

        await this._guard( async () =>
        {
            await this.planner.patchItem( this.planner.plan.id, itemId, { "dayDate" : dayDate, "sortOrder" : sortOrder } );
            this._render();
            this.app.view.toast( dayDate
                ? _lcs( "Verschoben auf {day}", { day : Planner.dayText( dayDate ) } )
                : _lcs( "Gemerkt — noch ohne Tag." ), "success" );
        }, _lcs( "Der Eintrag konnte nicht verschoben werden." ) );
    }

    /**
     * Moving an event off the day its date names: say when it really is and
     * let the guest decide. Resolves true when there is nothing to ask.
     */
    async _confirmOffDate( itemId, dayDate )
    {
        const realDay = this.planner.getDateConflict( itemId, dayDate );
        if ( !realDay )
        {
            return true;
        }

        const item = ( this.planner.plan.items || [] ).find( ( entry ) => entry.id === itemId );
        const row = Planner.toRow( item );
        const when = item.startTime
            ? _lcs( "{day} um {time} Uhr", { day : Planner.dayText( realDay ), time : item.startTime } )
            : Planner.dayText( realDay );

        return await this.app.view.confirm( {
            icon : "triangle-alert",
            title : _lcs( "Termin steht fest" ),
            text : _lcs( "„{title}“ findet am {when} statt. Trotzdem auf {day} legen?",
                { title : row.title, when : when, day : Planner.dayText( dayDate ) } ),
            confirmLabel : _lcs( "Trotzdem verschieben" ),
            danger : false
        } );
    }

    async _remove( itemId )
    {
        await this._guard( async () =>
        {
            await this.planner.removeItem( this.planner.plan.id, itemId );
            // The entry becomes offerable again, so the strip below is stale.
            await this.planner.reloadSuggestions( this.planner.plan.id );
            this._render();
            this.app.view.toast( _lcs( "Aus dem Planer entfernt." ), "info" );
        }, _lcs( "Der Eintrag konnte nicht entfernt werden." ) );
    }

    async _addSuggested( kind, recordId )
    {
        await this._guard( async () =>
        {
            const { item, isNew } = await this.planner.addItem( this.planner.plan.id, kind, recordId );
            await this.planner.reloadSuggestions( this.planner.plan.id );
            this._render();
            this.app.view.toast( Planner.addedText( item, isNew ), isNew ? "success" : "info" );
        }, _lcs( "Der Eintrag konnte nicht hinzugefügt werden." ) );
    }

    /**
     * A drag has landed. The rows are already where the guest dropped them, so
     * this does NOT re-render on success — repainting identical markup would
     * only cost the scroll position. A failure does re-render, from the plan as
     * the server still has it, which puts the row back where it really is.
     */
    async _onDragCommit( placements )
    {
        // Before the request, because the row is already visibly where it was
        // dropped and the prompt it landed on top of has to go with it.
        this._syncEmptyPrompts();

        // An event dragged away from its date: ask first. "Nein" puts the row
        // back by painting the board from the plan, which has not changed yet.
        const items = this.planner.plan.items || [];
        const moved = placements.find( ( placement ) =>
        {
            const item = items.find( ( entry ) => entry.id === placement.itemId );
            return item && item.dayDate !== placement.dayDate &&
                this.planner.getDateConflict( placement.itemId, placement.dayDate );
        } );
        if ( moved && !( await this._confirmOffDate( moved.itemId, moved.dayDate ) ) )
        {
            this._render();
            return;
        }

        try
        {
            await this.planner.commitLayout( this.planner.plan.id, placements );
        }
        catch ( e )
        {
            this.app.view.toast( e.message, "error" );
            await this.planner.open( this.planner.plan.id );
            this._render();
        }
    }

    /**
     * Show the "drop something here" prompt on exactly the lists that are now
     * empty.
     *
     * Needed because a drag deliberately does not re-render (see
     * _onDragCommit): the template decides the prompt's visibility from the
     * list as it was when the screen was painted, so without this a day that
     * just received its first entry keeps the prompt underneath it, and a day
     * whose last entry was dragged away keeps none.
     */
    _syncEmptyPrompts()
    {
        this.app.view.pageContent.querySelectorAll( "[data-drop]" ).forEach( ( list ) =>
        {
            const prompt = list.querySelector( "[data-empty]" );
            if ( prompt )
            {
                prompt.hidden = list.querySelector( "[data-item]" ) !== null;
            }
        } );
    }

    /**
     * Run one mutation, keeping a second from starting on top of it.
     *
     * The board's buttons stay live while a request is in flight (disabling
     * every row for a round trip is worse), so a fast tapper can otherwise
     * queue two writes whose responses arrive out of order and leave the screen
     * showing neither.
     */
    async _guard( action, fallbackMessage )
    {
        if ( this.busy )
        {
            return;
        }
        this.busy = true;

        try
        {
            await action();
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( e.message || fallbackMessage, "error" );
        }
        finally
        {
            this.busy = false;
        }
    }
}
